import { createElement as h, useEffect, useRef, useState, type ReactNode } from "react";
import { pickFile } from "../../../util/download";
import { fx, sc, scReturning, scVec3 } from "../inspector/bindings";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { InertTag, SectionDef } from "../inspector/schema";
import { CompactCheck, CompactColor, CompactNumber, CompactSlider, CompactText, MiniBtn, Row, TextActionInput } from "../inspector/rows";

/** `Row` requires `children` in props, so pass it explicitly. */
const row = (label: string, child: ReactNode, stacked?: boolean, inert?: InertTag) =>
  h(Row, { label, stacked, inert, children: child });

/** Progressive-shadow fields the baker accepts over RPC and never consumes. */
const PROG_COLOR_INERT: InertTag = {
  reason:
    "Stored, never read. GroundShadowBaker.applyGroundConfig consumes every other progressiveShadow field; the cone accumulates a mono occlusion value, so the colour never reaches a pixel.",
};
import type {
  GradientConfig,
  GroundConfigPatch,
  ProgressiveShadowLightParams,
  RandomizedSelfShadowParams,
  ScenePresetState,
  SceneState,
  Vec2,
  Vec3,
} from "../../../shared/api-contract";
import { useCaps } from "../CapsProvider";
import { useDomainState, useEditorStore } from "../../../state/editor/useEditor";
import { LightsControl } from "./custom/LightsControl";
import { EnvPresetSelect, StudioPresetSelect } from "./custom/SceneSelectors";
import { WorkspacePresets } from "./custom/WorkspacePresets";
import { GroundConfigControl } from "./custom/GroundConfigControl";
import { GradientControl } from "./custom/GradientControl";
import { HdriGroundConfigControl } from "./custom/HdriGroundConfigControl";
import { EnvAdvancedControl } from "./custom/EnvAdvancedControl";

/**
 * Scene tab — background/environment, ground & reflection, baked ground shadow,
 * tone mapping, and the full lighting system (studio presets, key/fill/hemi
 * rig, dynamic scene lights). Ported from the old SceneMenu/LightsMenu popovers.
 *
 * Most controls bind to SceneState scalars via sc() (optimistic, void setter).
 * The baked-shadow controls use scReturning() because setGroundShadowBake
 * returns the canonical settings object. Env-preset / studio-preset / tonemap
 * mode have no SceneState field, so they're small custom components that call
 * the RPC directly. The dynamic lights are one custom <LightsControl/> row.
 *
 * Ranges/steps are transcribed verbatim from the old menus.
 */

const PI = Math.PI;

/**
 * Progressive Shadow (Directional Light) — checklist verbatim title. Was only
 * a sub-header inside the Ground custom control (no standalone identity, and
 * missing the `visible`/`shadowEnabled` rows); this stands alone and adds
 * them. Reads/writes the same `GroundConfig.progressiveShadow` sub-object via
 * getGroundConfig/setGroundConfig — but each block seeds its own state once, so
 * the two copies drift apart the moment either one is edited.
 */
function ProgressiveShadowRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [p, setP] = useState<ProgressiveShadowLightParams | null>(null);
  // Mirrors `p` synchronously (unlike the state var, which only updates after
  // a render) so back-to-back edits — e.g. two vec3 axes changed before the
  // first RPC resolves — merge against the latest known value instead of a
  // stale render-time closure.
  const pRef = useRef<ProgressiveShadowLightParams | null>(null);
  useEffect(() => {
    r.getGroundConfig().then((g) => {
      pRef.current = g.progressiveShadow;
      setP(g.progressiveShadow);
    }).catch(() => {});
  }, [r]);
  if (!p) return null;
  const set = (patch: Partial<ProgressiveShadowLightParams>) => {
    pRef.current = pRef.current ? { ...pRef.current, ...patch } : pRef.current;
    return r.setGroundConfig({ progressiveShadow: patch } as GroundConfigPatch).then((g) => {
      pRef.current = g.progressiveShadow;
      setP(g.progressiveShadow);
    });
  };
  const chk = (label: string, val: boolean, key: keyof ProgressiveShadowLightParams) =>
    row(label, h(CompactCheck, { checked: val, onChange: (v: boolean) => void set({ [key]: v } as Partial<ProgressiveShadowLightParams>) }));
  const sld = (label: string, val: number, key: keyof ProgressiveShadowLightParams, min: number, max: number, step: number, inert?: InertTag) =>
    row(label, h(CompactSlider, { value: val, min, max, step, onChange: (v: number) => void set({ [key]: v } as Partial<ProgressiveShadowLightParams>) }), false, inert);
  const vec = (label: string, v: Vec3, min: number, max: number, key: "direction" | "normalDirection") =>
    row(
      label,
      h(
        "div",
        { className: "flex items-center gap-1" },
        ...(["x", "y", "z"] as (keyof Vec3)[]).map((ax) =>
          h(CompactNumber, {
            key: ax,
            value: v[ax],
            min,
            max,
            step: 0.001,
            width: 56,
            onChange: (nv: number) => {
              const cur = (pRef.current?.[key] as Vec3 | undefined) ?? v;
              void set({ [key]: { ...cur, [ax]: nv } } as Partial<ProgressiveShadowLightParams>);
            },
          }),
        ),
      ),
      true,
    );
  return h(
    "div",
    { className: "flex flex-col" },
    chk("Enabled", p.enabled, "enabled"),
    chk("Visible", p.visible, "visible"),
    chk("Shadow Enabled", p.shadowEnabled, "shadowEnabled"),
    sld("Intensity", p.intensity, "intensity", 0, 50, 0.1),
    row("Color", h(CompactColor, { value: p.color, onChange: (v: string) => void set({ color: v }) }), false, PROG_COLOR_INERT),
    sld("Focus", p.focus, "focus", 0, 1, 0.01),
    sld("Spread", p.spread, "spread", 0, 1, 0.01),
    sld("Distance Scale", p.distanceScale, "distanceScale", 0.01, 60, 0.01),
    vec("Direction", p.direction, -5, 5, "direction"),
    vec("Normal Direction", p.normalDirection, -1, 1, "normalDirection"),
    sld("Radius", p.radius, "radius", 0.01, 10, 0.1),
    sld("Frustum Size", p.frustumSize, "frustumSize", 0.01, 30, 0.01),
    sld("Bias", p.bias, "bias", -0.01, 0.01, 0.0001),
  );
}


/**
 * Randomized Self-Shadow (webgi `RandomizedDirectionalLightPlugin`, v32) — the
 * model's OWN cast shadow softened by jittering the key/self-shadow light
 * across refine frames, distinct from the Ground's baked shadow above (that
 * one bakes a static occlusion map; this one softens the three.js shadow map
 * the model itself casts). New RPC names ⇒ gated behind caps.v32.
 */
function RandomizedSelfShadowRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const r = ctx.ring;
  const [p, setP] = useState<RandomizedSelfShadowParams | null>(null);
  useEffect(() => {
    if (!caps.v32) return;
    r.getRandomizedSelfShadow().then(setP).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, caps.v32]);
  if (!caps.v32) {
    return h("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Needs viewer contract v32 or newer.");
  }
  if (!p) return null;
  const set = (patch: Partial<RandomizedSelfShadowParams>) => void r.setRandomizedSelfShadow(patch).then(setP);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Enabled", h(CompactCheck, { checked: p.enabled, onChange: (v: boolean) => set({ enabled: v }) })),
    row("Spread", h(CompactSlider, { value: p.spread, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ spread: v }) })),
    row("Focus", h(CompactSlider, { value: p.focus, min: 0, max: 1, step: 0.01, onChange: (v: number) => set({ focus: v }) })),
    row("Distance Scale", h(CompactSlider, { value: p.distanceScale, min: 0.01, max: 60, step: 0.01, onChange: (v: number) => set({ distanceScale: v }) })),
    row(
      "Direction",
      h(
        "div",
        { className: "flex items-center gap-1" },
        ...(["x", "y", "z"] as const).map((ax) =>
          h(CompactNumber, {
            key: ax,
            value: p.direction[ax],
            step: 0.01,
            width: 56,
            onChange: (nv: number) => set({ direction: { ...p.direction, [ax]: nv } }),
          }),
        ),
      ),
      true,
    ),
    h("div", { className: "px-3 pb-2 text-[11px] text-ed-text-3" }, "{0,0,0} direction follows the current key-light direction."),
  );
}

/** Scene Presets (v32, named `of-vjson/1` snapshots) — save the current scene
 * under a name, then re-apply it later; a lightweight in-viewer alternative
 * to a full vjson file export/import round-trip. New RPC names ⇒ gated
 * behind caps.v32. */
function ScenePresetsRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const r = ctx.ring;
  const [presets, setPresets] = useState<ScenePresetState[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => { r.listScenePresets().then(setPresets).catch(() => {}); };
  useEffect(() => { if (caps.v32) refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [r, caps.v32]);
  if (!caps.v32) {
    return h("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Needs viewer contract v32 or newer.");
  }
  const save = () => {
    const n = name.trim();
    if (!n) return;
    r.saveScenePreset(n).then(() => { setName(""); refresh(); }).catch(() => {});
  };
  const apply = (n: string) => { setBusy(n); void r.applyScenePreset(n).finally(() => setBusy(null)); };
  const del = (n: string) => void r.deleteScenePreset(n).then(refresh);
  return h(
    "div",
    { className: "flex flex-col gap-1.5 px-3 py-1.5" },
    h(
      "div",
      { className: "flex min-w-0 flex-1 gap-1" },
      h(CompactText, { value: name, placeholder: "Preset name…", onChange: setName }),
      h(
        "button",
        {
          onClick: save,
          className: "shrink-0 rounded border border-ed-border bg-ed-panel-2 px-2 text-[12px] font-medium text-ed-text hover:border-ed-accent",
        },
        "Save",
      ),
    ),
    presets.length === 0
      ? h("span", { className: "text-[11px] text-ed-text-3" }, "No saved presets yet.")
      : h(
          "ul",
          { className: "flex flex-col gap-1" },
          ...presets.map((p) =>
            h(
              "li",
              { key: p.name, className: "flex items-center gap-1.5 rounded border border-ed-border bg-white px-2 py-1" },
              h("span", { className: "min-w-0 flex-1 truncate text-[12px] text-ed-text" }, p.name),
              h(
                "button",
                { className: "shrink-0 text-[12px] text-ed-accent hover:underline", onClick: () => apply(p.name) },
                busy === p.name ? "Applying…" : "Apply",
              ),
              h(
                "button",
                { className: "shrink-0 text-[12px] text-ed-text-3 hover:text-red-500", onClick: () => del(p.name) },
                "Delete",
              ),
            ),
          ),
        ),
  );
}

/**
 * Gradient SVG — extra rows the checklist captures but `GradientControl`
 * (foreign file) doesn't render: master `enabled`, and the linear/radial
 * endpoint vectors (`position1`/`position2`/`center`/`focus`). Sits alongside
 * the existing custom row rather than replacing it. `enabled` gates
 * `generateGradient` server-side once the engine reads it; until then it
 * round-trips like the rest of `GradientConfig`.
 */
const GRADIENT_POS_INERT: InertTag = {
  reason:
    "Stored, never read. The linear branch of Viewer.generateGradient derives x1/y1/x2/y2 from `angle` alone; position1/position2 never reach the <linearGradient>. The radial Center/Focus rows below ARE read.",
};

function GradientExtraRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [g, setG] = useState<GradientConfig | null>(null);
  useEffect(() => {
    r.getGradientConfig().then(setG).catch(() => {});
  }, [r]);
  if (!g) return null;
  const gen = (patch: Partial<GradientConfig>) => r.generateGradient(patch).then(setG);
  const vec2 = (label: string, v: Vec2, key: "position1" | "position2" | "center" | "focus", inert?: InertTag) =>
    row(
      label,
      h(
        "div",
        { className: "flex items-center gap-1" },
        h(CompactNumber, { key: "x", value: v.x, step: 0.01, width: 56, onChange: (nv: number) => void gen({ [key]: { ...v, x: nv } }) }),
        h(CompactNumber, { key: "y", value: v.y, step: 0.01, width: 56, onChange: (nv: number) => void gen({ [key]: { ...v, y: nv } }) }),
      ),
      true,
      inert,
    );
  return h(
    "div",
    { className: "flex flex-col" },
    g.type === "linear" ? vec2("Position 1", g.position1, "position1", GRADIENT_POS_INERT) : null,
    g.type === "linear" ? vec2("Position 2", g.position2, "position2", GRADIENT_POS_INERT) : null,
    g.type === "radial" ? vec2("Center", g.center, "center") : null,
    g.type === "radial" ? vec2("Focus", g.focus, "focus") : null,
  );
}

/**
 * Background Image — `setBackgroundImageUrl` / `setBackgroundImageFile` have
 * shipped since contract v13, but the only ways to reach them were the V1
 * left-rail Background panel and a collapsed "(advanced)" button, so the
 * inspector had no image control at all.
 * Neither RPC takes options — the image is stretched over the frame, there is
 * no fit/cover knob to expose. New method names ⇒ gated behind
 * caps.backgroundImage.
 *
 * The viewer picks the background source that was set last, so loading an image
 * turns "Show Env as Background" off; re-seed the scene slice afterwards so the
 * rows above admit it.
 *
 * A picked FILE is inlined into `SceneState.backgroundImageUrl` as a data URL by
 * the viewer (contract v46), so it normally survives a save + reload like a URL
 * would. When the picture is too big for that cap the viewer reports an image
 * background with NO url — the one case where the backdrop on screen is local
 * only, and the one case the user has to be told about before they save.
 */
function BackgroundImageRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const store = useEditorStore();
  const scene = useDomainState("scene") as SceneState | undefined;
  const r = ctx.ring;
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const localOnly = scene?.backgroundSource === "image" && !scene?.backgroundImageUrl;
  if (!caps.backgroundImage) {
    return h("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Needs viewer contract v13 or newer.");
  }
  // Decoding is done inside the viewer, so hold the button for the round trip.
  const load = async (work: Promise<unknown>) => {
    setBusy(true);
    try {
      await work;
    } finally {
      setBusy(false);
      void store.refresh("scene");
    }
  };
  // pickFile swallows an onFile rejection, so surface it here; the URL field
  // renders its own failure line off the rejected promise.
  const pick = () =>
    void pickFile(".jpg,.jpeg,.png,.webp,.svg", async (f) => {
      try {
        await load(r.setBackgroundImageFile(f));
        setFailed(false);
      } catch {
        setFailed(true);
      }
    });
  return h(
    "div",
    { className: "flex flex-col" },
    row(
      "Background Image",
      h(TextActionInput, {
        placeholder: "Image URL\u2026",
        action: "Load",
        onSubmit: (url: string) => load(r.setBackgroundImageUrl(url)),
      }),
      true,
    ),
    h(
      "div",
      { className: "flex flex-wrap gap-1.5 px-3 pb-1.5" },
      h(MiniBtn, { icon: "image", busy, onClick: pick, children: "Choose image\u2026" }),
    ),
    failed ? h("span", { className: "block px-3 pb-1.5 text-[11px] text-ed-red" }, "Failed to load that image.") : null,
    localOnly
      ? h(
          "span",
          { className: "block px-3 pb-1.5 text-[11px] text-ed-orange" },
          "This image is too large to save with the scene — it will be gone after a reload. Load it from a URL to keep it.",
        )
      : null,
    h(
      "span",
      { className: "px-3 pb-1.5 text-[11px] text-ed-text-3" },
      "Fills the frame. Pick a Background colour above to remove it.",
    ),
  );
}

/**
 * "Use scene environment" — drop a Metal HDR override (contract v45). Loading a
 * Metal HDR sets an override flag that outranks `scene.environment` on every
 * metal AND survives every later Environment pick, so before this the first HDR
 * a user loaded here silently disconnected the Environment picker from the
 * metals until a page reload. New method name ⇒ gated behind caps.v45.
 *
 * Re-seeds the scene slice afterwards so `metalEnvUrl` (and the rotation / cap
 * rows that only mean something while an HDR is loaded) stop reporting the
 * override that is gone.
 */
function MetalEnvClearRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const caps = useCaps();
  const store = useEditorStore();
  const [busy, setBusy] = useState(false);
  if (!caps.v45) {
    return h("span", { className: "block px-3 py-1.5 text-[11px] text-ed-text-3" }, "Clearing a Metal HDR needs viewer contract v45 or newer.");
  }
  const clear = async () => {
    setBusy(true);
    try {
      await ctx.ring.clearMetalEnv();
    } finally {
      setBusy(false);
      void store.refresh("scene");
    }
  };
  // The way BACK to the boot HDR (v48). Until this existed there was none: the
  // first Environment pick disposes the entry project's metal HDR, and this
  // section offers only a URL box and a file picker — so the default was
  // unrecoverable short of a page reload, and the two rows below it went
  // silently dead at the same moment.
  const restore = async () => {
    setBusy(true);
    try {
      const had = await ctx.ring.restoreDefaultMetalEnv();
      if (!had) console.info("[scene] this viewer entry configured no default metal HDR");
    } finally {
      setBusy(false);
      void store.refresh("scene");
    }
  };
  return h(
    "div",
    { className: "flex flex-col" },
    h(
      "div",
      { className: "flex flex-wrap gap-1.5 px-3 py-1.5" },
      h(MiniBtn, { icon: "layers_clear", busy, onClick: () => void clear(), children: "Use scene environment" }),
      caps.v48
        ? h(MiniBtn, { icon: "restart_alt", busy, onClick: () => void restore(), children: "Restore default HDR" })
        : null,
    ),
    h(
      "span",
      { className: "px-3 pb-1.5 text-[11px] text-ed-text-3" },
      "Drops the override above so metals reflect the chosen Environment again.",
    ),
  );
}

/**
 * Why "Metal Rotation" and "Metal HDR Brightness Cap" go dead.
 *
 * Both setters are gated on a live metal HDR (`Viewer.setMetalEnvRotation` only
 * applies inside `if (hdr)`; `setMetalEnvMaxBrightness` early-returns from
 * `_regenerateMetalEnvFromSource` with no source). The FIRST Environment pick
 * calls `_dropDefaultMetalEnv`, which DISPOSES the boot HDR — so both rows went
 * inert without the user touching this section, while staying enabled and
 * happily reporting values back through SceneState. Saying so beats a control
 * that positively asserts a state the renderer is ignoring.
 */
const needsMetalHdr = (read: DomainRead): boolean | string => {
  const s = read("scene") as { metalEnvActive?: boolean };
  // Pre-v48 viewers do not report it; leave the rows alone rather than
  // disabling something that might well be live.
  if (s.metalEnvActive === undefined) return false;
  return s.metalEnvActive
    ? false
    : "No Metal HDR is loaded, so this does nothing. Load one above, or press Restore default HDR.";
};

export const sceneSections: SectionDef[] = [
  {
    // Ported from the Canvas chrome. Captures everything with a live getter AND
    // setter; the HDR image selection has no getter, so it is deliberately not
    // captured rather than captured wrong and silently restored as the default.
    id: "savedEnvironments",
    title: "Saved Environments",
    info:
      "Background, environment intensity and rotation, exposure and the key/fill/hemi rig, saved together. The HDR image itself is not captured — the viewer has no getter for which one is loaded.",
    defaultCollapsed: true,
    domains: ["scene"],
    rows: [
      {
        id: "savedEnv",
        kind: "custom",
        label: "",
        keywords: "Save as environment preset",
        component: () => h(WorkspacePresets, { kind: "env" }),
      },
    ],
  },
  {
    id: "environment",
    title: "Background / Environment",
    info: "The environment lights the whole scene and can double as the backdrop. Metals follow it unless a Metal HDR override is loaded (Lighting › Metal reflections map). Diamonds never do — they ray-trace against their own environment (Gem light).",
    rows: [
      // Three HDR slots surface under one search — this one, "Metal HDR
      // (override)" below, and the gem environment in the Gems panel — with
      // nothing saying how they relate. `metalEnvSource`, NOT `metalEnvActive`:
      // the latter is true for the BOOT metal HDR too, so keying off it would
      // announce an override the user never loaded.
      {
        id: "envSlotNote",
        kind: "note",
        label: "",
        keywords: "environment hdr scene lighting which slot metal gem",
        text: (read) =>
          // The standing explanation lives in the section's ⓘ; this line only
          // speaks when something is overriding it right now.
          (read("scene") as { metalEnvSource?: string }).metalEnvSource === "override"
            ? "Metals are ignoring this environment right now — a Metal HDR override is loaded (Metal reflections map) and wins for them."
            : "",
      },
      {
        id: "background",
        kind: "color",
        label: "Background",
        clearable: true,
        binding: sc(
          (s) => s.background,
          // Picking a colour (or clearing to transparent) makes THAT the
          // background source in the viewer — an env or image backdrop is
          // dropped. Patch the derived fields with it, or the rows below keep
          // reporting a background that is no longer on screen and Background
          // Blur stays enabled over a colour it cannot touch.
          (v) => ({
            background: v,
            backgroundSource: v ? ("color" as const) : ("transparent" as const),
            showEnvAsBackground: false,
            backgroundImageUrl: null,
          }),
          (r, v) => r.setBackground(v),
        ),
      },
      // The two flags that decide whether the colour above survives the composite.
      // They also live under Post Processing → Tonemapping, but that is a collapsed
      // section on another tab; both rows read the same postFX slice, so they stay in
      // sync. Off (our default) = WYSIWYG. On = the filmic backdrop iJewel ships, where
      // ACES rolls a #ffffff backdrop to ~#e2e2e2 but an HDR one keeps its roll-off.
      {
        id: "tonemapBg",
        kind: "checkbox",
        label: "Tonemap Background",
        hint: "Off: the picked colour renders exactly. On: ACES-rolled, so white reads grey.",
        binding: fx((s) => s.tonemapBackground, (v) => ({ tonemapBackground: v })),
      },
      {
        id: "tonemapGround",
        kind: "checkbox",
        label: "Tonemap Ground",
        hint: "Move with Tonemap Background — mismatched flags seam the frame at the horizon.",
        binding: fx((s) => s.tonemapGround, (v) => ({ tonemapGround: v })),
      },
      {
        id: "backgroundIntensity",
        kind: "slider",
        label: "Background Intensity",
        min: 0,
        max: 5,
        step: 0.01,
        binding: sc(
          (s) => s.backgroundIntensity,
          (v) => ({ backgroundIntensity: v }),
          (r, v) => r.setBackgroundIntensity(v),
        ),
      },
      { id: "backgroundImage", kind: "custom", label: "", keywords: "Background Image backdrop picture photo upload", component: BackgroundImageRow },
      {
        id: "envIntensity",
        kind: "slider",
        label: "Env Intensity",
        min: 0,
        max: 5,
        step: 0.01,
        binding: sc(
          (s) => s.envIntensity,
          (v) => ({ envIntensity: v }),
          (r, v) => r.setEnvIntensity(v),
        ),
      },
      { id: "envPreset", kind: "custom", label: "Environment", inline: true, keywords: "Environment HDR", component: EnvPresetSelect },
      {
        id: "envUrl",
        kind: "textAction",
        label: "Load HDR URL",
        placeholder: "HDR/EXR URL…",
        action: "Load",
        run: (url, ctx) => ctx.ring.loadEnvFromUrl(url),
        // Loading a scene environment runs _dropDefaultMetalEnv, so
        // metalEnvActive flips to false and the Metal HDR rows below must
        // re-read it — otherwise they stay enabled over setters that no-op.
        refreshDomains: ["scene"],
      },
      {
        id: "envFile",
        kind: "button",
        label: "Load HDR File",
        // Same as the URL row above: this disposes the boot metal HDR.
        refreshDomains: ["scene"],
        buttons: [
          {
            label: "Choose HDR/EXR…",
            icon: "upload",
            // RETURNED, not `void`ed — pickFile settles when its callback
            // settles, so discarding the promise made it resolve as soon as the
            // dialog closed and the refresh would have read the slice before
            // the environment had loaded.
            run: (ctx) => pickFile(".hdr,.exr", (f) => ctx.ring.loadEnvFromFile(f)),
          },
        ],
      },
      {
        id: "showEnvBg",
        kind: "checkbox",
        label: "Show Env as Background",
        binding: sc(
          (s) => s.showEnvAsBackground,
          // On, the env owns the background; off, the engine falls back to the
          // colour it still holds. (The box can only be on while the env IS the
          // source — every other background writer clears the flag.)
          (v) => ({ showEnvAsBackground: v, backgroundSource: v ? ("env" as const) : ("color" as const) }),
          (r, v) => r.setShowEnvAsBackground(v),
        ),
      },
      // An env background blurs through three's `backgroundBlurriness` (a PMREM
      // roughness lookup) and an image / gradient through the viewer's own
      // pyramid blur (v46). A flat colour and transparency have nothing to blur,
      // so the slider says so instead of silently doing nothing.
      {
        id: "bgBlur",
        kind: "slider",
        label: "Background Blur",
        hint: "Blurs an environment, image or gradient background. A flat colour has nothing to blur.",
        // Keys off the engine's own background source, not the
        // showEnvAsBackground mirror: every other background writer clears that
        // flag inside the viewer, so a colour picked after the checkbox used to
        // leave the slider live over a backdrop it could not touch.
        disabled: (read) => {
          const s = read("scene") as { showEnvAsBackground: boolean; backgroundSource?: string };
          // Pre-v46 viewers don't report the source, and back then only the env
          // background could blur at all.
          if (!s.backgroundSource) {
            return s.showEnvAsBackground
              ? false
              : "This viewer can only blur an environment backdrop — turn on Show Env as Background.";
          }
          if (s.backgroundSource === "env" || s.backgroundSource === "image") return false;
          return s.backgroundSource === "transparent"
            ? "Nothing to blur: the background is transparent. Load a background image or turn on Show Env as Background."
            : "Nothing to blur: the background is a flat colour. Load a background image or turn on Show Env as Background.";
        },
        min: 0,
        max: 1,
        step: 0.01,
        binding: sc(
          (s) => s.backgroundBlur,
          (v) => ({ backgroundBlur: v }),
          (r, v) => r.setBackgroundBlur(v),
        ),
      },
      {
        id: "envFollow",
        kind: "slider",
        label: "Env Follow Camera",
        // Measured: at strength 1 this moves 496k px of a 723k crop against a
        // 9.7k noise floor, and returns to the floor at 0 — it is not subtle.
        // But it only rotates the ENVIRONMENT, so on the default flat backdrop
        // the sole visible change is in the metal and gem reflections, which
        // reads as "nothing happened". Say where to look.
        hint: "Rotates the environment with the camera. On a flat background only the reflections change — turn on Show Env as Background to see it directly.",
        min: 0,
        max: 1,
        step: 0.01,
        binding: sc(
          (s) => s.envFollowCamera,
          (v) => ({ envFollowCamera: v }),
          (r, v) => r.setEnvFollowCamera(v),
        ),
      },
      {
        id: "envRotation",
        kind: "vec3",
        label: "Env Rotation",
        min: -PI,
        max: PI,
        step: 0.01,
        binding: scVec3(
          (s) => s.envRotation,
          "envRotation",
          (r, x, y, z) => r.setEnvRotation(x, y, z),
        ),
      },
    ],
  },
  {
    id: "bgEnvAdvanced",
    title: "Background / Environment (advanced)",
    defaultCollapsed: true,
    rows: [{ id: "envAdv", kind: "custom", label: "", component: EnvAdvancedControl }],
  },
  {
    id: "groundFull",
    title: "Ground",
    defaultCollapsed: true,
    rows: [{ id: "groundConfig", kind: "custom", label: "", component: GroundConfigControl }],
  },
  {
    id: "progressiveShadow",
    title: "Progressive Shadow (Directional Light)",
    defaultCollapsed: true,
    inert: {
      label: "duplicate",
      reason:
        "Works, but it drives the same GroundConfig.progressiveShadow sub-object as the Progressive Shadow block inside Scene > Ground. Both seed their own local copy once, so editing one leaves the other stale.",
    },
    rows: [{ id: "progressiveShadow", kind: "custom", label: "", component: ProgressiveShadowRow }],
  },
  {
    // The real engine-backed counterpart of the panel above (which is mostly
    // stored/round-trip) — jitters the model's OWN key/self-shadow light
    // instead of the Ground's. Same webgi RandomizedDirectionalLightPlugin
    // family, kept as its own section to avoid clashing with the Ground one.
    id: "randomizedSelfShadow",
    title: "Randomized Self-Shadow (Model)",
    defaultCollapsed: true,
    rows: [{ id: "rss", kind: "custom", label: "", component: RandomizedSelfShadowRow }],
  },
  {
    id: "gradientSvg",
    title: "Gradient SVG",
    defaultCollapsed: true,
    rows: [
      { id: "gradient", kind: "custom", label: "", component: GradientControl },
      { id: "gradientExtra", kind: "custom", label: "", component: GradientExtraRow },
    ],
  },
  {
    id: "hdriGround",
    title: "HDRi Ground",
    defaultCollapsed: true,
    rows: [{ id: "hdri", kind: "custom", label: "", component: HdriGroundConfigControl }],
  },
  {
    id: "generateGeometry",
    title: "Generate Geometry",
    defaultCollapsed: true,
    rows: [
      {
        id: "primitives",
        kind: "button",
        label: "",
        buttons: [
          { label: "Plane", icon: "crop_landscape", run: (ctx) => ctx.ring.generateGeometry({ kind: "plane", size: 2 }) },
          { label: "Box", icon: "deployed_code", run: (ctx) => ctx.ring.generateGeometry({ kind: "box", size: 1 }) },
          { label: "Sphere", icon: "circle", run: (ctx) => ctx.ring.generateGeometry({ kind: "sphere", radius: 0.6 }) },
          { label: "Circle", icon: "radio_button_unchecked", run: (ctx) => ctx.ring.generateGeometry({ kind: "circle", radius: 0.6 }) },
        ],
      },
      {
        id: "primitives2",
        kind: "button",
        label: "",
        buttons: [
          { label: "Torus", icon: "data_usage", run: (ctx) => ctx.ring.generateGeometry({ kind: "torus", radius: 0.6 }) },
          { label: "Cylinder", icon: "cylinder", run: (ctx) => ctx.ring.generateGeometry({ kind: "cylinder", radius: 0.4, height: 1 }) },
          { label: "Gem Sphere", icon: "diamond", run: (ctx) => ctx.ring.generateGeometry({ kind: "sphere", radius: 0.5, asGem: true }) },
        ],
      },
    ],
  },
  // ─── Extras below: not in the iJewel checklist (or superseded by the
  // checklist "Ground" section above), kept for OF functionality but pushed
  // past the checklist window so tab order matches the captured reference. ───
  {
    id: "metalEnv",
    title: "Metal HDR (override)",
    defaultCollapsed: true,
    rows: [
      {
        id: "metalEnvNote",
        kind: "note",
        label: "",
        keywords: "metal hdr override which slot environment",
        text: "Metals only, and it outranks the scene environment for them. Everything else keeps using the scene environment above; diamonds use the gem environment in the Gems panel.",
      },
      {
        id: "metalEnvUrl",
        kind: "textAction",
        label: "Load Metal HDR URL",
        placeholder: "Metal HDR/EXR URL…",
        action: "Load",
        run: (url, ctx) => ctx.ring.loadMetalEnvFromUrl(url),
        // Loading an override changes what the note above and the Rotation /
        // Brightness Cap gating below report. Nothing re-read the slice before
        // this, so those rows kept showing the state from panel mount.
        refreshDomains: ["scene"],
      },
      {
        id: "metalEnvFile",
        kind: "button",
        label: "Load Metal HDR File",
        // Sets metalEnvSource to "override"; the note above and the Rotation /
        // Brightness Cap gating below both read it.
        refreshDomains: ["scene"],
        buttons: [
          {
            label: "Choose HDR/EXR…",
            icon: "upload",
            run: (ctx) => pickFile(".hdr,.exr", (f) => ctx.ring.loadMetalEnvFromFile(f)),
          },
        ],
      },
      { id: "metalEnvClear", kind: "custom", label: "", keywords: "Metal Env Clear Reset HDR override", component: MetalEnvClearRow },
      {
        id: "metalEnvRotation",
        kind: "vec3",
        label: "Metal Rotation",
        disabled: needsMetalHdr,
        min: -PI,
        max: PI,
        step: 0.01,
        binding: scVec3(
          (s) => s.metalEnvRotation,
          "metalEnvRotation",
          (r, x, y, z) => r.setMetalEnvRotation(x, y, z),
        ),
      },
      {
        id: "metalEnvCap",
        kind: "slider",
        label: "Metal HDR Brightness Cap",
        disabled: needsMetalHdr,
        min: 0.1,
        max: 50,
        step: 0.1,
        format: (v) => (v >= 49 ? "∞" : v.toFixed(1)),
        binding: sc(
          (s) => (Number.isFinite(s.metalEnvMaxBrightness) ? s.metalEnvMaxBrightness : 50),
          (v) => ({ metalEnvMaxBrightness: v >= 49 ? Infinity : v }),
          (r, v) => r.setMetalEnvMaxBrightness(v >= 49 ? Infinity : v),
        ),
      },
    ],
  },
  {
    id: "ground",
    title: "Ground & Reflection (Legacy)",
    defaultCollapsed: true,
    rows: [
      {
        id: "contactShadow",
        kind: "checkbox",
        label: "Contact Shadow",
        inert: {
          label: "duplicate",
          reason:
            "Works, but setGroundShadow is a one-line alias for engine.setDepthShadow \u2014 the same flag behind \u201cGround Shadow (Baked) (Legacy) > Enable Shadow\u201d and Scene > Ground > Baked Shadows. Three rows, one engine flag.",
        },
        binding: sc(
          (s) => s.groundShadow,
          (v) => ({ groundShadow: v }),
          (r, v) => r.setGroundShadow(v),
        ),
      },
      {
        id: "reflection",
        kind: "checkbox",
        label: "Reflection",
        binding: sc(
          (s) => s.groundReflection,
          (v) => ({ groundReflection: v }),
          (r, v) => r.setGroundReflection(v),
        ),
      },
      {
        id: "reflectionStrength",
        kind: "slider",
        label: "Reflection Strength",
        min: 0,
        max: 2,
        step: 0.01,
        binding: sc(
          (s) => s.groundReflectionOpacity,
          (v) => ({ groundReflectionOpacity: v }),
          (r, v) => r.setGroundReflectionOpacity(v),
        ),
      },
      {
        id: "reflectionBlur",
        kind: "slider",
        label: "Reflection Blur",
        min: 0,
        max: 1,
        step: 0.01,
        binding: sc(
          (s) => s.groundReflectionBlur,
          (v) => ({ groundReflectionBlur: v }),
          (r, v) => r.setGroundReflectionBlur(v),
        ),
      },
    ],
  },
  {
    id: "groundShadowBake",
    title: "Ground Shadow (Baked) (Legacy)",
    defaultCollapsed: true,
    enabledBadge: sc(
      (s) => s.depthShadow,
      (v) => ({ depthShadow: v }),
      (r, v) => r.setDepthShadow(v),
    ),
    rows: [
      {
        id: "enable",
        kind: "checkbox",
        label: "Enable Shadow",
        inert: {
          label: "duplicate",
          reason:
            "Works, but it is the second of three rows driving engine.setDepthShadow (see Ground & Reflection (Legacy) > Contact Shadow and Ground > Baked Shadows).",
        },
        binding: sc(
          (s) => s.depthShadow,
          (v) => ({ depthShadow: v }),
          (r, v) => r.setDepthShadow(v),
        ),
      },
      {
        id: "darkness",
        kind: "slider",
        label: "Darkness",
        min: 0,
        max: 1.5,
        step: 0.01,
        binding: scReturning(
          "groundShadowBake",
          (g: { brightness: number }) => g.brightness,
          "brightness",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
          (v) => ({ depthShadowOpacity: v }),
        ),
      },
      {
        id: "softness",
        kind: "slider",
        label: "Softness",
        min: 0,
        max: 1,
        step: 0.01,
        binding: scReturning(
          "groundShadowBake",
          (g: { softness: number }) => g.softness,
          "softness",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
        ),
      },
      {
        id: "falloff",
        kind: "slider",
        label: "Falloff",
        min: 0.25,
        max: 4,
        step: 0.05,
        binding: scReturning(
          "groundShadowBake",
          (g: { falloff: number }) => g.falloff,
          "falloff",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
        ),
      },
      {
        id: "blur",
        kind: "slider",
        label: "Blur",
        min: 0,
        max: 8,
        step: 0.5,
        binding: scReturning(
          "groundShadowBake",
          (g: { blurRadius: number }) => g.blurRadius,
          "blurRadius",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
        ),
      },
      {
        id: "quality",
        kind: "slider",
        label: "Quality (samples)",
        min: 32,
        max: 400,
        step: 16,
        binding: scReturning(
          "groundShadowBake",
          (g: { sampleCount: number }) => g.sampleCount,
          "sampleCount",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
        ),
      },
      {
        id: "spread",
        kind: "slider",
        label: "Spread (plane size)",
        min: 0.5,
        max: 4,
        step: 0.1,
        binding: scReturning(
          "groundShadowBake",
          (g: { planeScale: number }) => g.planeScale,
          "planeScale",
          (api, patch) => Promise.resolve(api.setGroundShadowBake(patch)),
        ),
      },
      {
        id: "resolution",
        kind: "dropdown",
        label: "Resolution",
        options: [
          { id: "256", label: "256" },
          { id: "512", label: "512" },
          { id: "1024", label: "1024" },
        ],
        binding: scReturning<string, { resolution: number }>(
          "groundShadowBake",
          (g) => String(g.resolution),
          "resolution",
          (api, patch) =>
            Promise.resolve(api.setGroundShadowBake({ resolution: Number(patch.resolution) })),
        ),
      },
      {
        id: "rebake",
        kind: "button",
        label: "Re-bake",
        buttons: [
          { label: "Re-bake Shadow", icon: "refresh", run: (ctx) => ctx.ring.rebakeGroundShadow() },
        ],
      },
      {
        id: "lightHelper",
        kind: "checkbox",
        label: "Show Light Helper",
        inert: {
          label: "duplicate",
          reason:
            "Works, but setLightHelperVisible is the exact engine call behind Picking > Widgets > Visible, with no shared state between the two rows.",
        },
        hint: "Debug gizmo for the shadow-casting light direction.",
        binding: sc(
          (s) => s.lightHelperVisible,
          (v) => ({ lightHelperVisible: v }),
          (r, v) => r.setLightHelperVisible(v),
        ),
      },
    ],
  },
  {
    id: "studioPresets",
    title: "Studio Presets",
    defaultCollapsed: true,
    domains: ["scene"],
    rows: [
      {
        id: "studioPreset",
        kind: "custom",
        label: "Preset",
        domains: ["scene"],
        component: StudioPresetSelect,
      },
    ],
  },
  {
    id: "overallLighting",
    title: "Overall Lighting",
    defaultCollapsed: true,
    rows: [
      {
        id: "lightMultiplier",
        kind: "slider",
        label: "Light Multiplier",
        min: 0,
        max: 3,
        step: 0.01,
        binding: sc(
          (s) => s.lightMultiplier,
          (v) => ({ lightMultiplier: v }),
          (r, v) => r.setLightMultiplier(v),
        ),
      },
      {
        id: "selfShadows",
        kind: "checkbox",
        label: "Self Shadows",
        hint: "Meshes receive shadows from casting lights.",
        binding: sc(
          (s) => s.selfShadowing,
          (v) => ({ selfShadowing: v }),
          (r, v) => r.setSelfShadowing(v),
        ),
      },
    ],
  },
  {
    id: "keyLight",
    title: "Key Light",
    defaultCollapsed: true,
    rows: [
      {
        id: "keyIntensity",
        kind: "slider",
        label: "Intensity",
        min: 0,
        max: 10,
        step: 0.01,
        binding: sc(
          (s) => s.keyLightIntensity,
          (v) => ({ keyLightIntensity: v }),
          (r, v) => r.setKeyLightIntensity(v),
        ),
      },
      {
        id: "keyDirection",
        kind: "vec3",
        label: "Direction",
        min: -1,
        max: 1,
        step: 0.01,
        binding: scVec3(
          (s) => s.keyLightDirection,
          "keyLightDirection",
          (r, x, y, z) => r.setKeyLightDirection(x, y, z),
        ),
      },
      {
        id: "keyColor",
        kind: "color",
        label: "Colour",
        binding: sc(
          (s) => s.keyLightColor,
          (v) => ({ keyLightColor: v ?? "#ffffff" }),
          (r, v) => r.setKeyLightColor(v ?? "#ffffff"),
        ),
      },
    ],
  },
  {
    id: "fillLight",
    title: "Fill Light",
    defaultCollapsed: true,
    rows: [
      {
        id: "fillIntensity",
        kind: "slider",
        label: "Intensity",
        min: 0,
        max: 10,
        step: 0.01,
        binding: sc(
          (s) => s.fillLightIntensity,
          (v) => ({ fillLightIntensity: v }),
          (r, v) => r.setFillLightIntensity(v),
        ),
      },
      {
        id: "fillDirection",
        kind: "vec3",
        label: "Direction",
        min: -1,
        max: 1,
        step: 0.01,
        binding: scVec3(
          (s) => s.fillLightDirection,
          "fillLightDirection",
          (r, x, y, z) => r.setFillLightDirection(x, y, z),
        ),
      },
      {
        id: "fillColor",
        kind: "color",
        label: "Colour",
        binding: sc(
          (s) => s.fillLightColor,
          (v) => ({ fillLightColor: v ?? "#ffffff" }),
          (r, v) => r.setFillLightColor(v ?? "#ffffff"),
        ),
      },
    ],
  },
  {
    id: "hemiLight",
    title: "Ambient (Hemisphere)",
    defaultCollapsed: true,
    rows: [
      {
        id: "hemiIntensity",
        kind: "slider",
        label: "Intensity",
        min: 0,
        max: 3,
        step: 0.01,
        binding: sc(
          (s) => s.hemiLightIntensity,
          (v) => ({ hemiLightIntensity: v }),
          (r, v) => r.setHemiLightIntensity(v),
        ),
      },
      {
        id: "hemiSky",
        kind: "color",
        label: "Sky Colour",
        binding: sc(
          (s) => s.hemiSkyColor,
          (v) => ({ hemiSkyColor: v ?? "#ffffff" }),
          (r, v) => r.setHemiSkyColor(v ?? "#ffffff"),
        ),
      },
      {
        id: "hemiGround",
        kind: "color",
        label: "Ground Colour",
        binding: sc(
          (s) => s.hemiGroundColor,
          (v) => ({ hemiGroundColor: v ?? "#ffffff" }),
          (r, v) => r.setHemiGroundColor(v ?? "#ffffff"),
        ),
      },
    ],
  },
  {
    id: "sceneLights",
    title: "Scene Lights",
    defaultCollapsed: true,
    domains: ["scene"],
    rows: [
      { id: "lights", kind: "custom", label: "Lights", domains: ["scene"], component: LightsControl },
    ],
  },
  {
    // Named `of-vjson/1` scene snapshots (v32) — save the current look, apply
    // it back later. Sits last in Scene, next to the full vjson export/import
    // buttons in the Export tab.
    id: "scenePresets",
    title: "Scene Presets",
    defaultCollapsed: true,
    rows: [{ id: "presets", kind: "custom", label: "", component: ScenePresetsRow }],
  },
];
